import { ArrowRight, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { useContactForm } from "../hooks/useContactForm";
import {
    ACTIVITY_PAUSED,
    PAUSE_CONTACT_NOTICE,
} from "../lib/constants";

function ContactIntro() {
    return (
        <div className="lg:col-span-5">
            <p className="reveal text-xs tracking-[0.18em] uppercase text-slate-500 mb-4">
                05 — Prendre contact
            </p>
            <h2
                className="reveal font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0B132B] font-medium leading-[1.05] mb-8"
                data-testid="contact-title"
            >
                Une situation à clarifier&nbsp;?
            </h2>
            <p
                className="reveal text-base lg:text-lg text-slate-600 leading-relaxed max-w-md"
                data-testid="contact-intro"
            >
                Décrivez brièvement votre situation, votre activité et le
                point qui vous préoccupe. Je vous répondrai directement.
            </p>
        </div>
    );
}

function ContactSuccess({ successRef, onSendAnother }) {
    return (
        <div
            ref={successRef}
            className="border border-[#1B4332] bg-[#F9F9F8] p-8 sm:p-10"
            role="status"
            aria-live="polite"
            data-testid="contact-success"
        >
            <div className="flex items-start gap-4">
                <CheckCircle2
                    className="w-7 h-7 text-[#1B4332] shrink-0 mt-0.5"
                    strokeWidth={1.5}
                    aria-hidden="true"
                />
                <div className="flex-1">
                    <p className="font-display text-2xl sm:text-3xl text-[#0B132B] tracking-tight mb-3">
                        Demande envoyée.
                    </p>
                    <p className="text-base text-slate-700 leading-relaxed">
                        Merci pour votre message. Une réponse personnelle vous
                        parviendra sous quelques jours ouvrés à l'adresse
                        indiquée.
                    </p>
                    <p className="mt-4 text-sm text-slate-500 leading-relaxed">
                        Aucune information transmise à des tiers. Vous pouvez
                        à tout moment me joindre directement au{" "}
                        <a
                            href="tel:+33608033203"
                            className="text-[#0B132B] underline underline-offset-2 decoration-slate-300 hover:decoration-[#0B132B] tabular-nums whitespace-nowrap"
                        >
                            06 08 03 32 03
                        </a>
                        .
                    </p>
                    <button
                        type="button"
                        onClick={onSendAnother}
                        className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#0B132B] border-b border-[#0B132B] pb-0.5 hover:gap-2.5 transition-all"
                        data-testid="contact-send-another"
                    >
                        Envoyer un autre message
                        <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
}

function TextField({ id, label, type = "text", required, autoComplete, value, onChange, testId, disabled }) {
    return (
        <div>
            <label
                htmlFor={id}
                className="block text-xs tracking-[0.14em] uppercase text-slate-500 mb-2"
            >
                {label}
            </label>
            <input
                id={id}
                name={id}
                type={type}
                required={required}
                value={value}
                onChange={onChange}
                disabled={disabled}
                className="field-underline"
                autoComplete={autoComplete}
                data-testid={testId}
            />
        </div>
    );
}

function CaptchaField({ captcha, captchaError, value, onChange, disabled }) {
    return (
        <div>
            <label
                htmlFor="captcha-answer"
                className="block text-[10px] tracking-[0.14em] uppercase text-slate-400 mb-1"
            >
                Vérification anti spam
            </label>
            <p className="text-xs text-slate-500 mb-2">
                Combien font{" "}
                <span className="font-medium text-slate-700 tabular-nums">
                    {captcha.a} + {captcha.b}
                </span>
                {"\u00a0?"}
            </p>
            <input
                id="captcha-answer"
                name="captcha-answer"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                required
                value={value}
                onChange={onChange}
                disabled={disabled}
                className="field-underline max-w-[180px]"
                aria-describedby={captchaError ? "captcha-error" : undefined}
                data-testid="contact-input-captcha"
            />
            {captchaError && (
                <p
                    id="captcha-error"
                    className="mt-2 text-sm text-[#9B1C1C]"
                    data-testid="captcha-error"
                >
                    Réponse incorrecte. Un nouveau calcul vient d'être généré.
                </p>
            )}
        </div>
    );
}

function HoneypotField({ value, onChange }) {
    return (
        <>
            <input type="hidden" name="form-name" value="contact" />
            <p className="hidden">
                <label>
                    Ne pas remplir&nbsp;:{" "}
                    <input name="bot-field" value={value} onChange={onChange} />
                </label>
            </p>
        </>
    );
}

function MessageField({ value, onChange, disabled }) {
    return (
        <div>
            <label
                htmlFor="message"
                className="block text-xs tracking-[0.14em] uppercase text-slate-500 mb-2"
            >
                Message
            </label>
            <textarea
                id="message"
                name="message"
                required
                rows={5}
                value={value}
                onChange={onChange}
                disabled={disabled}
                className="field-underline"
                data-testid="contact-input-message"
            />
        </div>
    );
}

function SubmitButton({ status, disabled }) {
    const isDisabled = disabled || status === "sending";
    return (
        <div className="pt-2">
            <button
                type="submit"
                disabled={isDisabled}
                aria-disabled={isDisabled}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#0B132B] text-[#F9F9F8] text-sm font-medium rounded-full hover:bg-[#1B4332] transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-[#0B132B]"
                data-testid="contact-submit"
            >
                {status === "sending" ? "Envoi en cours…" : "Envoyer la demande"}
                <ArrowUpRight className="w-4 h-4" />
            </button>
        </div>
    );
}

function FormFields({ form, onChange, disabled }) {
    return (
        <>
            <TextField
                id="name"
                label="Nom"
                required
                autoComplete="name"
                value={form.name}
                onChange={onChange}
                disabled={disabled}
                testId="contact-input-name"
            />
            <TextField
                id="email"
                label="Email"
                type="email"
                required
                autoComplete="email"
                value={form.email}
                onChange={onChange}
                disabled={disabled}
                testId="contact-input-email"
            />
            <TextField
                id="structure"
                label="Structure ou activité"
                value={form.structure}
                onChange={onChange}
                disabled={disabled}
                testId="contact-input-structure"
            />
            <MessageField value={form.message} onChange={onChange} disabled={disabled} />
        </>
    );
}

function ContactPauseNotice() {
    return (
        <p
            className="mb-6 text-sm text-slate-600 border-l-2 border-[#1B4332] pl-4 py-1 leading-relaxed"
            role="status"
            data-testid="contact-pause-notice"
        >
            {PAUSE_CONTACT_NOTICE}
        </p>
    );
}

function ContactFormBody({ form, captcha, captchaError, status, onChange, onSubmit, disabled }) {
    return (
        <>
            {disabled && <ContactPauseNotice />}
            <form
                name="contact"
                method="POST"
                action="/merci"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={onSubmit}
                className="space-y-7"
                aria-disabled={disabled}
                data-testid="contact-form"
            >
                <fieldset disabled={disabled} className="space-y-7 border-0 p-0 m-0">
                    <HoneypotField value={form["bot-field"]} onChange={onChange} />
                    <FormFields form={form} onChange={onChange} disabled={disabled} />
                    <CaptchaField
                        captcha={captcha}
                        captchaError={captchaError}
                        value={form["captcha-answer"]}
                        onChange={onChange}
                        disabled={disabled}
                    />
                    <SubmitButton status={status} disabled={disabled} />
                </fieldset>
                {status === "error" && (
                    <p className="text-sm text-red-700" data-testid="contact-error">
                        Une erreur est survenue. Merci de réessayer dans un
                        instant.
                    </p>
                )}
            </form>
        </>
    );
}

export function Contact() {
    const {
        status,
        captcha,
        captchaError,
        form,
        successRef,
        handleChange,
        handleSubmit,
        reopenForm,
    } = useContactForm();

    return (
        <section
            id="contact"
            className="border-b border-slate-200 bg-white"
            data-testid="contact-section"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16 py-14 md:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <ContactIntro />
                    <div className="lg:col-span-6 lg:col-start-7">
                        {status === "success" ? (
                            <ContactSuccess
                                successRef={successRef}
                                onSendAnother={reopenForm}
                            />
                        ) : (
                            <ContactFormBody
                                form={form}
                                captcha={captcha}
                                captchaError={captchaError}
                                status={status}
                                onChange={handleChange}
                                onSubmit={handleSubmit}
                                disabled={ACTIVITY_PAUSED}
                            />
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
